import { reconcileAssessment } from './assessment-reconciliation';
import { assessmentHistory } from './assessment-data';
import { cityText, latestPurchase, recorderParties } from './property-feed';
import { parcelRecordings } from './property-records';
import { formatPropertyAddress } from './presentation';
import type { AssessorRecord, PropertySummary } from './types';

/**
 * Display priorities for the property fact cards built from shared assessor, planning,
 * recorder and parcel models. Skill fact cards declare their own priority on the same
 * 0–1000 scale; higher renders first.
 */
export const propertyFactCardPriority = {
  building: 900,
  value: 850,
  ownership: 800,
  planning: 700,
  parcel: 600,
} as const;

function numeric(value: string | undefined) {
  if (!value?.trim()) return null;
  const number = Number(value.replaceAll(',', ''));
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function assessorFactModel(assessor: AssessorRecord) {
  const fields = assessor.fields;
  const number = (key: string) => numeric(fields[key]) || null;
  const { conflicts, published } = reconcileAssessment(assessor);
  return {
    fields,
    conflicts,
    published,
    useType: fields['Use type'] ? cityText(fields['Use type']) : undefined,
    highlights: [
      { label: 'bedrooms', glyph: 'bed', value: number('Bedrooms') },
      { label: 'bathrooms', glyph: 'bath', value: number('Bathrooms') },
      { label: 'area', glyph: 'ruler', value: number('Building area'), unit: 'squareFeet' },
      {
        label: 'yearBuilt',
        glyph: 'calendar',
        value: number('Year built') ? fields['Year built'] : null,
      },
    ],
    details: [
      { label: 'units', value: number('Units') },
      { label: 'rooms', value: number('Rooms') },
      { label: 'stories', value: number('Stories') },
      { label: 'construction', value: cityText(fields['Construction type']) },
      { label: 'parcelArea', value: number('Parcel area'), unit: 'squareFeet' },
    ],
  };
}

export function assessmentValueModel(summary: PropertySummary) {
  const reconciliation = reconcileAssessment(summary.assessor, summary.recorder);
  const rows = assessmentHistory(summary.assessor?.history);
  const latest = rows[0]?.year !== null && !rows[0]?.ambiguous ? rows[0] : undefined;
  const diverged =
    (reconciliation.status === 'reassessed' || reconciliation.status === 'reduced') &&
    reconciliation.current !== null;
  return {
    reconciliation,
    rows,
    latest,
    diverged,
    values: diverged ? reconciliation.current : (latest?.values ?? reconciliation.current),
    exemption: numeric(latest?.roll.fields['Homeowner exemption value']),
  };
}

export function ownershipFactModel(summary: PropertySummary) {
  const recorder = summary.recorder;
  const purchase = latestPurchase(summary);
  const parties = purchase ? recorderParties(purchase.names) : null;
  const documents = parcelRecordings(recorder);
  const loans = documents.filter((document) =>
    document.titles.some((title) => /^DEED OF TRUST/i.test(title.trim())),
  ).length;
  return { recorder, purchase, parties, documents, loans };
}

export function parcelFactModel(summary: PropertySummary) {
  const fields = summary.assessor?.fields ?? {};
  return {
    blockLot: `${summary.block} / ${summary.lot}`,
    parcelNumber: summary.assessor?.parcelNumber || fields['Parcel number'] || '',
    assessorAddress: fields['Assessor address']
      ? formatPropertyAddress(fields['Assessor address'])
      : undefined,
  };
}
